import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Divider,
  Link,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import GroupsIcon from "@mui/icons-material/Groups";
import BadgeIcon from "@mui/icons-material/Badge";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { BASE_PATH_TO_HOME } from "../../../base_routes_paths/data_paths";
import { BASE_PATH_TO_CHALLENGE_PARTICIPANTS } from "../../../base_routes_paths/corporate_challenges_paths";
import AUTH_CONTEXT from "../../../store";
import ARENO_API_ENDPOINT from "../../../api";

const PRIMARY = "#4f005f";

// ─── Challenge Card ───────────────────────────────────────────────────────────

const ChallengeCard = ({ challenge, onShowParticipants }) => (
  <Paper
    elevation={0}
    sx={{
      width: 480,
      borderRadius: 3,
      border: "1px solid",
      borderColor: "divider",
      overflow: "hidden",
    }}
  >
    {/* Card header */}
    <Box
      sx={{
        px: 4,
        py: 3,
        background: `linear-gradient(135deg, ${PRIMARY} 0%, #7b1fa2 100%)`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 2,
            backgroundColor: "rgba(255,255,255,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <LeaderboardIcon sx={{ color: "#fff", fontSize: 28 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            color="#fff"
            noWrap
            lineHeight={1.3}
          >
            {challenge.Name}
          </Typography>
          <Chip
            label={challenge.IsStarted ? "Started" : "Not Started"}
            size="small"
            sx={{
              mt: 0.5,
              height: 24,
              fontSize: "0.8rem",
              fontWeight: 700,
              backgroundColor: challenge.IsStarted
                ? "rgba(76,175,80,0.25)"
                : "rgba(255,255,255,0.15)",
              color: challenge.IsStarted ? "#a5d6a7" : "rgba(255,255,255,0.85)",
            }}
          />
        </Box>
      </Stack>
    </Box>

    {/* Card body */}
    <Box sx={{ px: 4, py: 3, backgroundColor: "#fafafa" }}>
      <Stack spacing={2}>
        {/* Corporate ID */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <BadgeIcon sx={{ fontSize: 22, color: "text.disabled", flexShrink: 0 }} />
          <Box>
            <Typography variant="body2" color="text.secondary" display="block">
              Corporate ID
            </Typography>
            <Typography variant="body1" fontWeight={600} color="text.primary">
              {challenge.CorporateID}
            </Typography>
          </Box>
        </Stack>

        <Divider />

        {/* Total Participants */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <PeopleAltIcon sx={{ fontSize: 22, color: "text.disabled", flexShrink: 0 }} />
          <Box>
            <Typography variant="body2" color="text.secondary" display="block">
              Total Participants
            </Typography>
            <Typography variant="body1" fontWeight={600} color="text.primary">
              {challenge.TotalParticipants}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Box>

    {/* Card footer */}
    <Box sx={{ px: 4, py: 2.5, borderTop: "1px solid", borderColor: "divider" }}>
      <Button
        fullWidth
        variant="contained"
        endIcon={<ArrowForwardIcon fontSize="medium" />}
        onClick={onShowParticipants}
        sx={{
          backgroundColor: PRIMARY,
          fontWeight: 700,
          fontSize: "1rem",
          py: 1.5,
          "&:hover": { backgroundColor: "#3a0047" },
        }}
      >
        Show Participants
      </Button>
    </Box>
  </Paper>
);

// ─── Skeleton Card ────────────────────────────────────────────────────────────

const SkeletonCard = () => (
  <Paper
    elevation={0}
    sx={{
      width: 480,
      borderRadius: 3,
      border: "1px solid",
      borderColor: "divider",
      overflow: "hidden",
    }}
  >
    <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${PRIMARY}88 0%, #7b1fa288 100%)` }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 2, bgcolor: "rgba(255,255,255,0.2)" }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="70%" sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />
          <Skeleton variant="rounded" width={80} height={20} sx={{ mt: 0.5, bgcolor: "rgba(255,255,255,0.15)" }} />
        </Box>
      </Stack>
    </Box>
    <Box sx={{ px: 3, py: 2.5, backgroundColor: "#fafafa" }}>
      <Stack spacing={1.5}>
        <Skeleton variant="text" width="50%" />
        <Divider />
        <Skeleton variant="text" width="40%" />
      </Stack>
    </Box>
    <Box sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
      <Skeleton variant="rounded" height={36} />
    </Box>
  </Paper>
);

// ─── Main component ───────────────────────────────────────────────────────────

const NormalLeaderboard = () => {
  const { token } = AUTH_CONTEXT();
  const navigate = useNavigate();

  const [challenges, setChallenges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchChallenges = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${ARENO_API_ENDPOINT}/challenges/AdminViewCompetetiveChallenge`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await response.json();
        if (response.status === 200 && data.status === 200) {
          setChallenges(data.data);
        } else {
          toast.error(data.error || "Failed to load challenges", {
            position: "bottom-left",
            autoClose: 3000,
            hideProgressBar: true,
          });
        }
      } catch (error) {
        toast.error(error.message, {
          position: "bottom-left",
          autoClose: 5000,
          theme: "light",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchChallenges();
  }, [token]);

  return (
    <Box sx={{ px: 3, py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link underline="hover" color="inherit" href={BASE_PATH_TO_HOME}>
          Dashboard
        </Link>
        <Link underline="hover" color="inherit">
          Corporate Challenges
        </Link>
        <Typography color="text.primary" fontWeight={500}>
          Participants
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={4}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            backgroundColor: PRIMARY,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <GroupsIcon sx={{ color: "#fff", fontSize: 22 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} lineHeight={1.2} color="#181818">
            Arm Wrestling Participants
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and assign points to challenge participants
          </Typography>
        </Box>
      </Stack>

      {/* Cards */}
      <Stack direction="row" flexWrap="wrap" gap={3} justifyContent="center">
        {isLoading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : challenges.length === 0 ? (
          <Box
            sx={{
              width: "100%",
              py: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              color: "text.secondary",
            }}
          >
            <LeaderboardIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            <Typography variant="body1" fontWeight={500}>
              No challenges found
            </Typography>
          </Box>
        ) : (
          challenges.map((challenge) => (
            <ChallengeCard
              key={challenge.CompetetiveChallengeID}
              challenge={challenge}
              onShowParticipants={() =>
                navigate(`${BASE_PATH_TO_CHALLENGE_PARTICIPANTS}/${challenge.CorporateID}`)
              }
            />
          ))
        )}
      </Stack>
    </Box>
  );
};

export default NormalLeaderboard;
